/**
 * ONE READER FOR TWO RUNG SHAPES — the request half of S4b, kept pure so it can be armed.
 *
 * ⛔ THE TWO ROUTES DO NOT SERVE THE SAME OBJECT, AND `rungLadder.ts` SAYS SO IN ITS OWN WORDS:
 * *"At sub-minute, price and CVD arrive on the SAME row from ONE statement, so a pane must not run
 * its two-leg reconciliation there — there is no second leg to reconcile."* `rungSource().oneRow`
 * is that property, already declared. This module does the reading; it does not re-decide routing.
 *
 * ⛔ AND THE FIELD NAMES DIVERGE, MEASURED AGAINST BOTH DEPLOYED ROUTES 2026-08-24 18:39–18:5xZ:
 * ```
 *   /api/es/cvd?tf=1     t: number 1787597340000   · no o/h/l/c   · instrument
 *   /api/es/cvd/submin   bar_open: ISO STRING      · o/h/l/c      · instrument_id
 *                        ** and NO `t` at all, until bi's f8e1948 deploys **
 * ```
 * ⇒ ***A CONSUMER THAT BRANCHES ON THE RUNG IN FIVE PLACES WILL EVENTUALLY BRANCH WRONG IN ONE.***
 * The branch happens once, here, and `barTimeMs` is the single reader of the instant either way.
 */

import { rungSource, type Rung } from "./rungLadder.ts";
import { drawableBars, barTimeMs, type RungBar, type RungEnvelope } from "./rungFeed.ts";

/**
 * The URL for a rung's payload. Built from `rungSource`, never from a hand-written map — a second
 * table is exactly the "constant that equals its own universe" defect `rungFeed.ts` banks.
 *
 * ⚠ `limit` is CLAMPED, not trusted: the worker's own MAX_LIMIT is 1500, and a caller that asks for
 * more gets a capped payload whose `capped` flag it may not read. Asking honestly beats being cut.
 */
export const MAX_LIMIT = 1500;

export function rungUrl(r: Rung, limit: number, symbol = "ES"): string {
  const src = rungSource(r);
  /**
   * ⛔ `NaN` IS NOT CLAMPABLE, AND THE CLAMP LOOKED LIKE IT HANDLED IT — `codex review` [P2].
   * `Math.max(1, Math.min(1500, Math.floor(NaN)))` is `NaN`, so a viewport arithmetic slip becomes
   * the literal request `limit=NaN`. ⇒ ***A BOUNDARY THAT SILENTLY FORWARDS THE ONE VALUE IT WAS
   * BUILT TO BOUND IS WORSE THAN NO BOUNDARY***, because callers stop checking. Refuse LOUDLY and
   * locally: a malformed request reaching the worker is a defect discovered one venue too late.
   * ⚠ My own suite covered 0, -5 and 12.7 and never NaN — the fixture set missed the only input
   * that survives the clamp. Same shape as the zone-guard hole earlier this shift.
   */
  if (!Number.isFinite(limit)) {
    throw new RangeError(`rungUrl: limit must be finite, got ${limit} (rung ${src.param}=${src.value})`);
  }
  const n = Math.max(1, Math.min(MAX_LIMIT, Math.floor(limit)));
  return `${src.path}?symbol=${encodeURIComponent(symbol)}&${src.param}=${encodeURIComponent(src.value)}&limit=${n}`;
}

/** What a rung answered with: the honesty envelope, its bars, and whether they carry price. */
export interface RungPayload {
  readonly env: RungEnvelope;
  readonly bars: readonly RungBar[];
  /** True when the rows carry o/h/l/c — i.e. price and flow on ONE row (sub-minute). */
  readonly oneRow: boolean;
  /**
   * ⛔ THE ROOT THE RESPONSE ECHOED — LAW 2: *"every row, query and panel carries its root. Never
   * pool them."* `codex review` [P2], and it is a law, not a nicety: I was discarding `root` and
   * handing consumers rootless bars, which is precisely how an SPX row and an SPXW row become
   * indistinguishable downstream. `null` means the route made NO claim, which is not the same as
   * agreeing with the request.
   */
  readonly root: string | null;
  /**
   * ⛔ DID THE ROUTE SERVE THE RUNG WE ASKED FOR? — `codex review` [P1]. A worker that ignores
   * `tf`/`span`, or a cached response for another rung, would otherwise be labelled with the
   * SELECTED rung: default-resolution bars wearing the name of the one the owner clicked.
   * `CvdResponse.tf_applied` exists in this app precisely because that has happened.
   * ⚠ `null` = the route echoed nothing ⇒ **NO MISMATCH CLAIM**, never "verified". That is
   * `api.ts`'s own rule for `tf_applied` and `rungFeed`'s for `counts`: absence is not agreement.
   */
  readonly appliedMatches: boolean | null;
  /** What the route said it served, verbatim, so a chip can name it rather than imply it. */
  readonly appliedEcho: string | null;
}

/**
 * ⛔ AN UNPARSEABLE OR MIS-SHAPED PAYLOAD YIELDS `null`, NEVER AN EMPTY PAYLOAD.
 *
 * `{ bars: [] }` is a route saying "no buckets"; `null` is a route that did not answer in this
 * shape at all. Collapsing the two would let a 200-with-HTML — which `rungLadder`'s own docblock
 * warns about at the `pages.dev` venue, where every `/api` path returns the SPA shell — read as a
 * quiet session. ⇒ ***"IT ANSWERED NOTHING" AND "IT DID NOT ANSWER" MUST NOT SHARE A VALUE.***
 */
export function readRungPayload(json: unknown, r: Rung): RungPayload | null {
  if (json === null || typeof json !== "object") return null;
  const o = json as Record<string, unknown>;
  if (!Array.isArray(o.bars)) return null;
  const bars = o.bars.filter((b): b is RungBar => b !== null && typeof b === "object");
  // A row that survived the filter but is not a bucket shape is still not a bar.
  if (bars.length !== o.bars.length) return null;
  const env: RungEnvelope = {
    stale: typeof o.stale === "boolean" ? o.stale : null,
    age_s: typeof o.age_s === "number" ? o.age_s : null,
    empty: typeof o.empty === "boolean" ? o.empty : null,
    empty_reason: typeof o.empty_reason === "string" ? o.empty_reason : null,
    study_status: typeof o.study_status === "string" ? o.study_status : null,
    counts: o.counts !== null && typeof o.counts === "object" ? (o.counts as RungEnvelope["counts"]) : null,
  };
  const src = rungSource(r);
  // The echo lives under a DIFFERENT key per route: `span` sub-minute, `tf_applied` minute+.
  // Read the one this rung's route actually serves; never fall back to the other, because a
  // missing echo read through the wrong key would look like a mismatch that never happened.
  const echoRaw = src.param === "span" ? o.span : o.tf_applied;
  const appliedEcho = typeof echoRaw === "string" ? echoRaw : null;
  return {
    env,
    bars,
    oneRow: src.oneRow,
    root: typeof o.root === "string" ? o.root : null,
    appliedEcho,
    appliedMatches: appliedEcho === null ? null : appliedEcho === src.value,
  };
}

/**
 * Does the payload's root match what was asked? `null` = the route made no claim.
 * ⚠ SEPARATE FROM `appliedMatches` ON PURPOSE — a right rung on the wrong root and a wrong rung on
 * the right root are different defects, and a single boolean would hide which one you have.
 */
export function rootMatches(p: RungPayload, expected = "ES"): boolean | null {
  return p.root === null ? null : p.root === expected;
}

/**
 * Does this payload carry drawable PRICE? Sub-minute rows do; minute rows do not (price comes from
 * `/api/es/candles`). ⚠ Asked of the PAYLOAD, not of the rung: a route that stops serving `o/h/l/c`
 * must make the pane go loud, not make it trust `oneRow` and draw an empty price pane.
 */
export function carriesPrice(p: RungPayload): boolean {
  /**
   * ⛔ `"c" in b` WAS NOT THE PREDICATE — `codex review` [P2]. A row keeping `c` while losing
   * `o`/`h`/`l`, or carrying a NULL `c`, passed it, so a partial schema regression hid behind a
   * healthy-looking capability flag and the pane would draw no candle under a full header.
   * ⭐ AND THE CURE IS REUSE, NOT A NEW PREDICATE (ratchet #2): `barIsGap` already rules exactly
   * this — *"o/h/l/c arrive NULL; anything that coerces them paints a candle at 0.00"* — and
   * `drawableBars` is the set that survives it. A second OHLC rule here would be a second source of
   * truth about what a candle is, which is the defect `rungFeed`'s own header warns about.
   */
  return p.oneRow && drawableBars(p.bars).length > 0;
}

// -------------------------------------------------------------------------------------------------
// S4c — ONE SUB-MINUTE PAYLOAD BECOMES THE THREE THINGS A PANE TAKES.
// -------------------------------------------------------------------------------------------------

/**
 * ⛔ AT SUB-MINUTE THERE IS NO SECOND LEG TO RECONCILE. `/api/es/candles` has no rung below a
 * minute, so price and flow arrive on ONE row and one payload must yield candles, closes and CVD.
 * `rungSource().oneRow` is that property; this is the adaptation it implies.
 *
 * ⭐ NOTHING HERE RE-JUDGES ANYTHING ANOTHER MODULE ALREADY RULES:
 * ```
 *   the instant of a bar ....... barTimeMs      (one reader, prefers `t`)
 *   whether it is a candle ..... drawableBars   (o/h/l/c all present)
 *   whether it may be drawn .... admitCvd       (LAW 2 lives there, not here)
 * ```
 * ⇒ a fourth opinion about any of those would be a second source of truth.
 */

/** The candles pane 0 draws. GAPS ARE LEFT OUT, never zero-filled — a 0.00 print is invented. */
export function subminCandles(p: RungPayload): { t: number; o: number; h: number; l: number; c: number }[] {
  const out: { t: number; o: number; h: number; l: number; c: number }[] = [];
  for (const b of drawableBars(p.bars)) {
    const t = barTimeMs(b);
    if (t === null) continue; // untimeable: dropped here, COUNTED by timeBinding
    if (b.ambiguous === true) continue; // see `bucketRefusal` — the producer's flag wins
    out.push({ t, o: b.o as number, h: b.h as number, l: b.l as number, c: b.c as number });
  }
  return out;
}

/**
 * The closes the CVD lane reconciles against. ⚠ A GAP KEEPS ITS ROW WITH `c: null` rather than
 * vanishing: `PriceBar.c` is nullable precisely so a bucket with no priced tick stays on the axis
 * as an absence. Dropping it would close the gap silently, which is the trap `barIsGap` exists for.
 */
export function subminPrice(p: RungPayload): { t: number; c: number | null }[] {
  const out: { t: number; c: number | null }[] = [];
  for (const b of p.bars) {
    const t = barTimeMs(b);
    if (t === null) continue;
    // An ambiguous bucket keeps its SLOT and loses its VALUE — an absence on the axis, not a
    // vanished minute. Dropping the row would close the gap; drawing `c` would render a print
    // the producer refused to stand behind.
    const refused = b.ambiguous === true;
    out.push({ t, c: !refused && typeof b.c === "number" ? b.c : null });
  }
  return out;
}

/**
 * The envelope `admitCvd` judges. ⛔⛔ THE ROOT IS PASSED THROUGH, NEVER DEFAULTED — if the route
 * named no root this leaves it UNDEFINED so `cvd2BlockedReason` refuses the pane, which is the
 * correct outcome under law 2: *"an unlabelled feed is not a neutral feed."* Writing `"ES"` here
 * would be this module making a root claim the DATA never made.
 * ⚠ `ok` is DERIVED, because the sub-minute route does not serve it: having a readable payload at
 * all IS the answer to "did it answer". An empty one is `ok` and carries its own reason.
 */
export function subminCvdFeed(p: RungPayload): {
  ok: boolean; root?: string; bars: { t: number; cvd?: number | null;
  cvd_o?: number | null; cvd_h?: number | null; cvd_l?: number | null; delta?: number | null;
  n_signed?: number | null; n_unsigned?: number | null; instrument?: string | null;
  instrument_count?: number | null; ambiguous?: boolean | null;
    // ORDER 77 R1403 — the served convention, declared on the RETURN shape too. This function's
    // bar type is written out THREE times (return signature, local array, pushed literal); a
    // field added to one and not the others is a compile error here only because all three live
    // in one file. The same field crossing a PACKAGE boundary got no such help.
    session_key?: string | null; session_cvd_before?: number | null }[];
  study_status?: string | null; age_s?: number | null; stale?: boolean; reason?: string | null;
} {
  const bars: { t: number; cvd?: number | null;
                cvd_o?: number | null; cvd_h?: number | null; cvd_l?: number | null; delta?: number | null;
                n_signed?: number | null; n_unsigned?: number | null; instrument?: string | null;
                instrument_count?: number | null; ambiguous?: boolean | null;
                session_key?: string | null; session_cvd_before?: number | null }[] = [];
  for (const b of p.bars) {
    const t = barTimeMs(b);
    if (t === null) continue;
    const refusal = bucketRefusal(b);
    bars.push(refusal === null
      ? { t, cvd: b.cvd ?? null,
          cvd_o: b.cvd_o ?? null, cvd_h: b.cvd_h ?? null, cvd_l: b.cvd_l ?? null,
          delta: b.delta ?? null,
          n_signed: b.n_signed ?? null, n_unsigned: b.n_unsigned ?? null,
          instrument: b.instrument ?? b.instrument_id ?? null,
          instrument_count: b.instrument_count ?? null, ambiguous: b.ambiguous ?? null,
          // ⛔⛔ ORDER 77's SERVED CONVENTION, CARRIED ACROSS THE HOP IT WAS BEING DROPPED ON.
          // This enumeration is a REBUILD, not a spread: every field the pane sees must be named
          // here or it does not survive. The worker serves session_key on every submin row
          // (index.ts :2476) and `servedAnchorState` reads its PRESENCE as the whole anchor
          // discriminator (cvd2.ts :164) — so omitting it here made three rungs draw UNANCHORED
          // over anchored values and log the done-test's forbidden line.
          // ⇒ *** AN ENUMERATING MAPPER IS A DENYLIST WITH NO DEFAULT: a field added upstream is
          //   dropped SILENTLY, and the failure appears at the far end as a wrong STATE, not as a
          //   missing field. *** The spread that would have carried it was rejected upstream for
          //   good reason (a bucket's refused values must not survive), so the cure is naming.
          session_key: b.session_key ?? null,
          session_cvd_before: b.session_cvd_before ?? null }
      // ⛔ REFUSED: the SLOT survives, the VALUES do not. `cvdCandles` draws nothing from a null
      // value, which is the point — a carry-forward `cvd` with `delta: 0` would draw a CONFIDENT
      // FLAT candle out of a bucket that measured nothing.
      : { t, cvd: null, delta: null, n_signed: b.n_signed ?? null, n_unsigned: b.n_unsigned ?? null,
          instrument: b.instrument ?? b.instrument_id ?? null,
          instrument_count: b.instrument_count ?? null, ambiguous: b.ambiguous ?? null });
  }
  return {
    ok: true,
    ...(p.root === null ? {} : { root: p.root }),
    bars,
    study_status: p.env.study_status ?? null,
    age_s: p.env.age_s ?? null,
    stale: p.env.stale ?? undefined,
    reason: p.env.empty_reason ?? null,
  };
}

/**
 * ⛔⛔ THE REFUSAL LADDER, TAKEN FROM THE CONSUMER THAT ALREADY PAID FOR IT — `cvdLane.ts:207-221`.
 * `codex review` filed all four of these as [P1] and every one reproduces: my adapter had become a
 * BYPASS around guards this app already enforces elsewhere.
 * ```
 *   1 the route's `ambiguous` flag WINS. `submin.ts` marks it only for GENUINE ambiguity
 *     (two vendor labels for one instrument_id) and says: blank the rung.
 *   2 SIGNING-ABSENT: n_signed === 0 with n_unsigned > 0. The `cvd` is a CARRY-FORWARD and
 *     `delta: 0` means nothing was signable — `cvdLane` pushes null here, and a flat candle
 *     drawn from it is a confident claim of balanced flow made from no signed ticks at all.
 * ```
 * ⇒ ***AN ADAPTER THAT RE-ADMITS WHAT AN ESTABLISHED CONSUMER REFUSES IS NOT A NEW READER, IT IS A
 * HOLE IN AN OLD GUARD*** — and it is invisible, because the guard it bypasses still passes its own
 * tests. Returns the REASON, so a chip can say which refusal fired rather than just showing a gap.
 */
export function bucketRefusal(b: RungBar): string | null {
  if (b.ambiguous === true) return "ambiguous — two vendor labels for one instrument";
  if (b.n_signed === 0 && typeof b.n_unsigned === "number" && b.n_unsigned > 0) {
    return "signing-absent — ticks arrived, none could be signed";
  }
  return null;
}

/** Every input a sub-minute pane needs, or a single reason why it may draw none of them. */
export interface SubminAdmission {
  readonly inputs: {
    readonly candles: { t: number; o: number; h: number; l: number; c: number }[];
    readonly price: { t: number; c: number | null }[];
    readonly cvdFeed: ReturnType<typeof subminCvdFeed>;
  } | null;
  readonly blocked: string | null;
  /** Per-bucket refusals that did NOT block the pane — the chip's evidence. */
  readonly refused: number;
}

/**
 * ⛔⛔ ALL THREE LEGS ARE GATED ON ONE IDENTITY DECISION — `codex review` [P1], and the hole was
 * real: `admitCvd` refuses only the CVD leg, while `SessionCvdPane` renders its PRICE leg
 * independently under a HARD-CODED `ES · price + session CVD` header. So a foreign-root payload
 * had its flow blocked and its PRICE drawn, labelled ES, by the pane's frame.
 * ⇒ ***REFUSING ONE LEG OF A TWO-LEG PANE IS NOT REFUSING THE PANE.*** Law 2 is about the panel,
 * not about one series in it.
 * ⚠ AND THE RUNG IS PART OF IDENTITY TOO: a payload whose echo disagrees with the rung we asked
 * for is 30s bars wearing the 5s label. `readRungPayload` measures that; discarding the
 * measurement here is what made the field decorative. `null` (no echo) is NOT a mismatch.
 */
export function admitSubmin(p: RungPayload, expectRoot = "ES"): SubminAdmission {
  const refused = p.bars.filter((b) => bucketRefusal(b) !== null).length;
  if (p.root === null) {
    return { inputs: null, refused, blocked: "the rung route served no `root` — this pane will not label another root's data as " + expectRoot };
  }
  if (p.root !== expectRoot) {
    return { inputs: null, refused, blocked: `the rung route served root=${p.root}, this pane renders ${expectRoot} — refusing to pool roots` };
  }
  if (p.appliedMatches === false) {
    return { inputs: null, refused, blocked: `the route served rung ${p.appliedEcho}, not the one selected — refusing to label it as the selected rung` };
  }
  return { inputs: { candles: subminCandles(p), price: subminPrice(p), cvdFeed: subminCvdFeed(p) }, blocked: null, refused };
}
